import './FetchFilterBar.scss';
import cn from 'classnames';
import { Category, FilterPaneState } from '@blisspointmedia/bpm-types/dist/FilterPane';
import * as R from 'ramda';
import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useState } from 'react';
import { pluralize } from 'inflected';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { measureTextWidth } from '@src/utils/canvasTextUtils';
import { COMMON_CATEGORY_LABELS } from './FilterPaneBasic';
import { Button } from '../Button/Button';
import { Appearance, Placement, Size } from '../designConstants';

// Hardcoded styles that match the CSS - keep these in sync with SCSS
const CHIP_STYLES = {
  fontSize: '1rem',
  fontFamily: 'Geist',
  fontWeight: '500',
  paddingLeft: 16,
  paddingRight: 16
};

const CHIP_FONT = `${CHIP_STYLES.fontWeight} ${CHIP_STYLES.fontSize} ${CHIP_STYLES.fontFamily}`;

interface FetchFilterBarChipProps {
  advanced?: string;
  category?: Category;
  expanded?: boolean;
  filterState?: FilterPaneState;
  setInitialOpenSection?: Dispatch<SetStateAction<string>>;
  setShowEditFilterModal?: Dispatch<SetStateAction<boolean>>;
  containerRef?: RefObject<HTMLDivElement | null>;
  groupNames?: string[];
}

export const FetchFilterBarChip = ({
  advanced,
  category,
  expanded,
  filterState,
  setInitialOpenSection = () => {},
  setShowEditFilterModal = () => {},
  containerRef,
  groupNames
}: FetchFilterBarChipProps) => {
  const [containerWidth, setContainerWidth] = useState<number>(0);
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef?.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [containerRef]);

  const pluralizeLabel = useCallback((label: string) => {
    if (label.includes(' of ')) {
      return [pluralize(label.split(' of ')[0]), label.split(' of ')[1]].join(' of ');
    }
    if (label.toLowerCase().includes(' vs ') || label.toLowerCase().includes(' vs.')) {
      return 'selected';
    }
    return pluralize(label);
  }, []);

  const getItemsThatFit = useCallback(
    (items: string[], prefix: string, categoryLabel: string) => {
      // Use container width if available, otherwise fall back to window width calculation
      const effectiveWidth = containerWidth || window.innerWidth;
      const maxChipWidth = effectiveWidth * 0.3333;
      const categoryWidth = measureTextWidth(`${categoryLabel}:`, CHIP_FONT);
      const prefixWidth = measureTextWidth(prefix, CHIP_FONT);
      const moreTextWidth = measureTextWidth(' + 99 more', CHIP_FONT);
      const padding = CHIP_STYLES.paddingLeft + CHIP_STYLES.paddingRight;
      const availableWidth = maxChipWidth - categoryWidth - prefixWidth - moreTextWidth - padding;
      let currentWidth = 0;
      let visibleCount = 0;
      for (let i = 0; i < items.length; i++) {
        const itemText = i === 0 ? items[i] : `, ${items[i]}`;
        const itemWidth = measureTextWidth(itemText, CHIP_FONT);
        if (currentWidth + itemWidth > availableWidth) {
          break;
        }
        currentWidth += itemWidth;
        visibleCount++;
      }
      return {
        visibleItems: items.slice(0, visibleCount),
        hiddenCount: Math.max(0, items.length - visibleCount)
      };
    },
    [containerWidth]
  );

  const disabled = Boolean(category?.key) && R.toLower(category?.key ?? '') === 'disabled';

  if (advanced) {
    const tokenCategory = <div className='token-category'>Advanced:</div>;
    const advancedContent = <div className='token-items'>{advanced.length ? advanced : 'All'}</div>;
    const tokenContent = (
      <div className='token-content'>
        {tokenCategory}
        {advancedContent}
      </div>
    );
    const chip = (
      <div className='token-chip'>
        <Button
          size={Size.LG}
          onClick={() => {
            setInitialOpenSection('Advanced');
            setShowEditFilterModal(true);
          }}
          appearance={Appearance.OUTLINED}
          className='token-chip-button advanced'
        >
          {tokenContent}
        </Button>
      </div>
    );

    return chip;
  }
  if (category) {
    const tokenCategory = <div className='token-category'>{category.label}:</div>;
    const selectedTokenItems: string[] = [];
    let not = false;

    if (filterState && filterState.basic && filterState.basic.selectedMap) {
      const { selectedMap } = filterState.basic;
      if (groupNames?.includes(category.key)) {
        for (const option of category.options) {
          const segmentName = typeof option === 'string' ? option : option.value || option.label;
          if (selectedMap[segmentName] && selectedMap[segmentName].Yes === true) {
            selectedTokenItems.push(segmentName);
            // Check if this segment has exclude set in notMap
            if (!not && filterState.basic.notMap && filterState.basic.notMap[segmentName]) {
              not = true;
            }
          }
        }
      } else {
        not =
          filterState && filterState.basic && filterState.basic.notMap
            ? filterState.basic.notMap[category.key]
            : false;
        for (const selectedKey of R.keys(R.defaultTo({}, selectedMap[category.key]))) {
          if (selectedMap[category.key][selectedKey]) {
            if (
              COMMON_CATEGORY_LABELS[category.key] &&
              COMMON_CATEGORY_LABELS[category.key][selectedKey]
            ) {
              selectedTokenItems.push(COMMON_CATEGORY_LABELS[category.key][selectedKey]);
            } else {
              selectedTokenItems.push(selectedKey);
            }
          }
        }
      }
    }
    const tokenItems = [...selectedTokenItems];
    const prefix = not ? 'Exclude ' : '';
    let contentString;
    const { visibleItems, hiddenCount } = getItemsThatFit(tokenItems, prefix, category.label);
    if (!expanded && hiddenCount > 0) {
      // Not expanded and not all/no items fit? Show count only
      contentString = `${not ? 'Exclude' : ''} ${tokenItems.length} ${
        hiddenCount === 1
          ? category.label.toLowerCase()
          : pluralizeLabel(category.label.toLowerCase())
      }`;
    } else if (expanded) {
      if (hiddenCount > 0) {
        if (visibleItems.length > 0) {
          // Expanded and not all items fit? show items that fit + hidden count
          contentString = `${prefix}${visibleItems.join(', ')} + ${hiddenCount} more`;
        } else {
          // Expanded and no items fit? Show count only
          contentString = `${not ? 'Exclude' : ''} ${tokenItems.length} ${
            hiddenCount === 1
              ? category.label.toLowerCase()
              : pluralizeLabel(category.label.toLowerCase())
          }`;
        }
      } else {
        // Expanded and all items fit? Show all items
        contentString = `${prefix}${visibleItems.join(', ')}`;
      }
    } else {
      // Not expanded and all items fit? Show all items
      contentString = `${prefix}${visibleItems.join(', ')}`;
    }

    const tokenItemsContent = <div className='token-items'>{contentString}</div>;
    const tokenContent = (
      <div className='token-content'>
        {tokenCategory}
        {tokenItemsContent}
      </div>
    );
    const chip = (
      <div className='token-chip'>
        <Button
          size={Size.LG}
          disabled={disabled}
          onClick={() => {
            setInitialOpenSection(category.key);
            setShowEditFilterModal(true);
          }}
          appearance={Appearance.OUTLINED}
          className={cn('token-chip-button', { 'chip-disabled': disabled })}
        >
          {tokenContent}
        </Button>
      </div>
    );

    const tooltipContent = (
      <Tooltip id='token-chip-tooltip' className='token-chip-tooltip'>
        <div>
          {tokenItems.map((item, index) => (
            <div key={index}>{item}</div>
          ))}
        </div>
      </Tooltip>
    );

    if (disabled) {
      return (
        <OverlayTrigger
          overlay={
            <Tooltip id='disabled-chip-tooltip'>
              This filter is currently disabled with the current dimension settings.
            </Tooltip>
          }
        >
          {chip}
        </OverlayTrigger>
      );
    }
    if (hiddenCount > 0) {
      return (
        <OverlayTrigger overlay={tooltipContent} placement={Placement.BOTTOM_START}>
          {chip}
        </OverlayTrigger>
      );
    }
    return chip;
  }
  return <div />;
};
